import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Chip,
  Stack,
} from "@mui/material";

function SetValueModal({ open, variable, meta, onClose, onConfirm }) {
  const [value, setValue] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setValue("");
      setError("");
      setSubmitting(false);
    }
  }, [open, variable]);

  if (!open) return null;

  const label = meta?.display_name || variable || "Variable";
  const dataType = meta?.data_type || "float";
  const minValue = meta?.min_value;
  const maxValue = meta?.max_value;
  const unit = meta?.unit;

  const validate = () => {
    if (value === "") {
      return "Please enter a value";
    }

    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) {
      return "Please enter a valid number";
    }

    if (dataType === "int" && !Number.isInteger(numericValue)) {
      return "Please enter an integer";
    }

    if (dataType === "bool" && ![0, 1].includes(numericValue)) {
      return "Boolean values must be 0 or 1";
    }

    if (minValue !== null && minValue !== undefined && numericValue < minValue) {
      return `Value must be at least ${minValue}`;
    }

    if (maxValue !== null && maxValue !== undefined && numericValue > maxValue) {
      return `Value must be at most ${maxValue}`;
    }

    return "";
  };

  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSubmitting(true);
      setError("");
      await onConfirm(variable, Number(value));
    } catch (err) {
      setError(err.message || "Failed to set value");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: "var(--radius-md)",
          border: "1px solid var(--border-color)",
          backgroundColor: "var(--bg-elevated)",
          boxShadow: "var(--shadow-medium)",
          backgroundImage: "none",
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, color: "var(--text-main)", fontWeight: 750 }}>
        Set Variable
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Stack spacing={1.25} sx={{ mb: 2.5 }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "var(--text-main)" }}>
              {label}
            </Typography>
            {meta?.variable_name && meta.variable_name !== label && (
              <Typography variant="body2" sx={{ color: "var(--text-muted)" }}>
                Write key: {meta.variable_name}
              </Typography>
            )}
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip size="small" label={`Type • ${dataType}`} variant="outlined" />
            {unit ? <Chip size="small" label={`Unit • ${unit}`} variant="outlined" /> : null}
            {(minValue !== null && minValue !== undefined) ||
            (maxValue !== null && maxValue !== undefined) ? (
              <Chip
                size="small"
                label={`Range • ${minValue ?? "-∞"} to ${maxValue ?? "∞"}`}
                variant="outlined"
              />
            ) : null}
          </Stack>
        </Stack>

        <TextField
          autoFocus
          fullWidth
          label="New value"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          error={!!error}
          helperText={error || " "}
          disabled={submitting}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 0.5 }}>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {submitting ? "Setting..." : "Confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default SetValueModal;